/**
 * <drawer-element> — a panel that slides in over the page.
 *
 * Wraps a native <dialog> opened with showModal(), which gives us the focus trap,
 * Escape-to-close, the ::backdrop pseudo-element and inert-ing of the rest of the
 * page for free. Scroll locking comes from the `scroll-lock` attribute, matched by
 * the `html:has(dialog[scroll-lock][open])` rule in critical.css.
 *
 * Open it from anywhere with a `data-drawer-open="<id>"` attribute; the listener is
 * delegated, so markup rendered later (Section Rendering API responses, for example)
 * keeps working without rebinding.
 */
/* Wrapped in an IIFE so its top-level names stay out of the global scope.
   Theme scripts share one global namespace: when two of them declared the same
   const, the second failed to parse outright and silently did nothing. */
(() => {
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.dialog.addEventListener('click', this.#onDialogClick);
      this.dialog.addEventListener('close', this.#onClose);

      this.querySelectorAll('[data-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
    }

    disconnectedCallback() {
      this.dialog?.removeEventListener('click', this.#onDialogClick);
      this.dialog?.removeEventListener('close', this.#onClose);
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      // Remember where focus came from so it can be restored on close.
      this.opener = document.activeElement;
      this.dialog.showModal();
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      this.dialog?.close();
    }

    /**
     * A modal <dialog> fills the viewport, so a click on the visible backdrop still
     * lands on the dialog itself. Compare against its box to tell the two apart.
     */
    #onDialogClick = (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= box.left &&
        event.clientX <= box.right &&
        event.clientY >= box.top &&
        event.clientY <= box.bottom;
      if (!inside) this.close();
    };

    #onClose = () => {
      this.opener?.focus();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    };
  }

  if (!customElements.get('drawer-element')) {
    customElements.define('drawer-element', DrawerElement);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;

    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!(drawer instanceof DrawerElement)) return;

    event.preventDefault();
    drawer.open();
  });
})();
